"use client"

import { useEffect, useRef } from "react"

import { useStore } from "@/app/components/common/store.provider"

const MIN_INTERVAL_MS = 30_000

/**
 * When the tab/window gains focus, validate the auth token via GET /auth/me.
 * Invalid tokens are cleared silently (no toasts).
 */
export function useSessionFocusValidation() {
  const { authStore } = useStore()
  const inFlightRef = useRef(false)
  const lastCheckAtRef = useRef(0)

  useEffect(() => {
    const runValidation = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return
      }
      if (!authStore.isAuthenticated || !authStore.tokens) {
        return
      }
      if (inFlightRef.current) {
        return
      }

      const now = Date.now()
      if (now - lastCheckAtRef.current < MIN_INTERVAL_MS) {
        return
      }

      inFlightRef.current = true
      lastCheckAtRef.current = now
      void authStore
        .validateSessionOnFocus()
        .catch(() => {
          // Never surface errors — validation failures that aren't auth stay logged in
        })
        .finally(() => {
          inFlightRef.current = false
        })
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runValidation()
      }
    }

    const onFocus = () => {
      runValidation()
    }

    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("focus", onFocus)

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("focus", onFocus)
    }
  }, [authStore])
}
